import React, { useState, useRef } from 'react';
import { useFloating, offset, flip, shift, arrow, useClick, useDismiss, useInteractions } from '@floating-ui/react';
import tooltipIcon from "../../assets/icons/infoTooltip.svg";
import popOverInfoIcon from "../../assets/icons/whiteInfoIcon.svg";
import morningIcon from "../../assets/icons/whiteMorningIcon.svg";
import earlyAfternoonIcon from "../../assets/icons/whiteEarlyAfternoonIcon.svg";
import lateAfternoonIcon from "../../assets/icons/whiteLateAfternoonIcon.svg";
import eveningIcon from "../../assets/icons/whiteEveningIcon.svg";
import { ReactComponent as EllipsisIcon } from "../../assets/icons/whiteEllipsisIcon.svg";
import styles from "./DayPartPopOver.module.css";
import { DayPart } from '../../gql/types';

function renderDayPartIcon(dp: DayPart) {
    let iconImg;
    switch (dp) {
        case DayPart.Morning:
            iconImg = <img src={morningIcon} />;
            break;
        case DayPart.EarlyAfternoon:
            iconImg = <img src={earlyAfternoonIcon} />;
            break;
        case DayPart.LateAfternoon:
            iconImg = <img src={lateAfternoonIcon} />;
            break;
        case DayPart.Evening:
            iconImg = <img src={eveningIcon} />;
            break;
        default:
            return null;
    }

    return (
        <span className={styles.ellipsisWrapper}>
            <EllipsisIcon className={styles.ellipsisIcon} />
            <span className={styles.innerDayPartIcon}>
                {iconImg}
            </span>
        </span>);
}

interface DayPartPopOverProps { }

const DayPartPopOver: React.FC<DayPartPopOverProps> = () => {
    const [open, setOpen] = useState(false);

    const arrowRef = useRef<HTMLDivElement | null>(null);

    const {
        x,
        y,
        strategy,
        refs,
        middlewareData,
        context,
    } = useFloating({
        placement: 'bottom-start',
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift(), arrow({ element: arrowRef })],
    });
    // Create interactions (click to open/close + outside press to dismiss)
    const click = useClick(context, {
        // `toggle: true` means each click on the reference toggles the popover
        toggle: true,
        // If you want it to open on the first click, then close on second click, set this to true
    });
    const dismiss = useDismiss(context, {
        outsidePress: true, // close popover on outside click
        ancestorScroll: true,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
    const referenceProps = getReferenceProps({
        onMouseDown: (e) => {
            // Prevent the default focus behavior
            e.preventDefault();
        },
    });

    const arrowX = middlewareData.arrow?.x ?? 0;
    const arrowY = middlewareData.arrow?.y ?? 0;

    return (
        <div>
            {/* Attach the reference to the button using refs.setReference */}
            <button ref={refs.setReference} {...referenceProps} className={styles.tooltipButton} type="button">
                <img src={tooltipIcon} className={styles.tooltipIcon} />
            </button>

            {open && (
                // Attach the floating ref to the popover div using refs.setFloating
                <div
                    ref={refs.setFloating}
                    {...getFloatingProps()}
                    className={styles.popover}
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        zIndex: 9999,
                    }}
                >
                    {/* <div
                        ref={arrowRef}
                        style={{
                            position: 'absolute',
                            left: arrowX,
                            top: arrowY,
                            // A simple CSS arrow: 
                            width: '10px',
                            height: '10px',
                            transform: 'translate(10%, -15%) rotate(45deg)',
                            backgroundColor: '#2F1C0A',
                        }}
                    >
                    </div> */}
                    <div className={styles.header}>
                        <img src={popOverInfoIcon} className={styles.popOverInfoIcon} />
                        <div className={styles.headerTitle}>
                            Time range
                        </div>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.contentRow}>
                            <div className={styles.contentRowLeft}>
                                {renderDayPartIcon(DayPart.Morning)}
                                Morning
                            </div>
                            <div className={styles.contentRowRight}>
                                9:00 AM - 12:00 PM
                            </div>
                        </div>
                        <div className={styles.contentRow}>
                            <div className={styles.contentRowLeft}>
                                {renderDayPartIcon(DayPart.EarlyAfternoon)}
                                Early Afternoon
                            </div>
                            <div className={styles.contentRowRight}>
                                12:00 PM - 3:00 PM
                            </div>
                        </div>
                        <div className={styles.contentRow}>
                            <div className={styles.contentRowLeft}>
                                {renderDayPartIcon(DayPart.LateAfternoon)}
                                Late Afternoon
                            </div>
                            <div className={styles.contentRowRight}>
                                3:00 PM - 6:00 PM
                            </div>
                        </div>
                        <div className={styles.contentRow}>
                            <div className={styles.contentRowLeft}>
                                {renderDayPartIcon(DayPart.Evening)}
                                Evening
                            </div>
                            <div className={styles.contentRowRight}>
                                6:00 PM - 8:00 PM
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DayPartPopOver;
